/**
 * /community/members/<handle>/stats — the hub's Stats tab (Member Stats design 1a–1i, contract
 * 2026-10-11), PUBLIC: readers per month and per day, time spent reading, readers by owner
 * review, points and rank, gear vs other owners, month by month. The same server half as every
 * hub page (`memberServerProps`: handle check, lowercase 308 to THIS page, the member and summary
 * reads, the community edge cache) plus DealHunt's stats read (`{ stats: true }`), the same
 * `noindex, follow`.
 *
 * A stats read that fails (503 `stats_unavailable` before DealHunt's db:push, a 404, a blip)
 * renders the tab with "Stats aren't available yet" — never a 404 or a 500; the member page
 * stands. The HTML is the visitor page; the member's own coaching hydrates inside `MemberStats`.
 */
import { useEffect } from 'react'
import type { GetServerSideProps } from 'next'
import { publicConfig } from 'config'
import { memberStatsHref } from 'lib/community/model'
import { markStatsSeen } from 'lib/community/menu-stats'
import { memberServerProps, type MemberPageData } from 'lib/community/member-page'
import { CommunityShell, type CommunityPageProps } from 'lib/community/page'
import { MemberHub } from 'components/community/MemberHub'
import { MemberStats } from 'components/community/MemberStats'
import styles from 'components/community/community.module.css'

const MemberStatsPage: React.VFC<CommunityPageProps<MemberPageData>> = ({
  common,
  ts,
  data: { member, guide, facts, lastRankedPoints, stats = null },
}) => {
  const trailingSlash = publicConfig('useTrailingSlash')
  // The avatar menu's Stats entry drops its "NEW" once this browser has opened a Stats tab (B.2).
  useEffect(markStatsSeen, [])
  return (
    <CommunityShell
      common={common}
      ts={ts}
      path={memberStatsHref(member.handle, trailingSlash)}
      title={`${member.handle} · Stats`}
      description={`Readers, time spent reading and points for ${member.handle}’s owner reviews in the SoundGuys community.`}
      index={false}
    >
      <div className={styles.page}>
        <MemberHub member={member} page="stats" homeHref={common.homeLink.href}>
          <MemberStats
            member={member}
            stats={stats}
            guide={guide}
            facts={facts}
            lastRankedPoints={lastRankedPoints}
          />
        </MemberHub>
      </div>
    </CommunityShell>
  )
}

export const getServerSideProps: GetServerSideProps<CommunityPageProps<MemberPageData>> = context =>
  memberServerProps(context, memberStatsHref, { stats: true })

export default MemberStatsPage
